/**
 * Cloud implementation of the shared VehicleBlacklistApi. Node-scoped: the watchlist lives on
 * the node that matches against it, so every call binds the currently-selected node id the
 * same way the attendance and search adapters do.
 *
 * With no node picked the reads answer empty and the writes refuse, rather than throwing an
 * unhandled rejection into the shared panel — a cloud operator who has not chosen a site
 * should see an empty list, not an error.
 */
import type {
  VehicleBlacklistApi,
  VehicleBlacklistEntry,
  VehicleBlacklistPage,
} from '@trinetra/shared';
import * as svc from '../services/vehicleBlacklist.service';
import { selectedNodeId as nodeId } from './node';

const nid = () => nodeId() || '';

function requireNode(): string {
  const id = nid();
  if (!id) throw new Error('Pick a node before editing its vehicle watchlist.');
  return id;
}

export const cloudVehicleBlacklistApi: VehicleBlacklistApi = {
  async list(params) {
    if (!nid()) return { data: [] } as VehicleBlacklistPage;
    const res = await svc.listVehicleBlacklist(nid(), params);
    return {
      data: Array.isArray(res?.data) ? (res.data as VehicleBlacklistEntry[]) : [],
      pagination: res?.pagination,
    } as VehicleBlacklistPage;
  },

  add: (input) => svc.addVehicleBlacklist(requireNode(), input),
  update: (id, patch) => svc.updateVehicleBlacklist(requireNode(), id, patch),

  async remove(id) {
    await svc.deleteVehicleBlacklist(requireNode(), id);
  },

  check: (plate) => svc.checkVehicleBlacklist(requireNode(), plate),
};
